// v1
// Path: frontend/src/components/SyncHub/shared/DiffAnalyzerView.jsx
// Inputs: numerous props from FieldMappingDesigner
// Outputs: The Diff / Preview Analyzer sub-view for pipelines.
// Summary: Extracted DiffAnalyzerView to split FieldMappingDesigner.

import React from 'react';
import DiffMetricCards from './DiffMetricCards';

export default function DiffAnalyzerView(props) {
    const {
        loadingPreview,
        diffPreviewData,
        diffDetailTab,
        setDiffDetailTab,
        selectedQBEntity,
        handleGenerateDiff
    } = props;

    return (
        <div className="flex-1 overflow-y-auto bg-slate-900 p-4">
            <div className="max-w-4xl mx-auto space-y-4">
                <div className="flex justify-between items-center bg-slate-850 p-4 rounded-xl border border-slate-750">
                    <div>
                        <h4 className="text-sm font-bold text-white">Target Impact Analysis</h4>
                        <p className="text-[11px] text-slate-400 mt-1">
                            Analyzes Passport source data against existing {selectedQBEntity} records to calculate updates and creations.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleGenerateDiff}
                        disabled={loadingPreview}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-2 shadow-md disabled:opacity-50"
                    >
                        {loadingPreview ? (
                            <>
                                <span className="material-symbols-outlined text-[16px] animate-spin">sync</span>
                                Analyzing...
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined text-[16px]">troubleshoot</span>
                                Generate Impact Diff
                            </>
                        )}
                    </button>
                </div>

                {loadingPreview && (
                    <div className="p-8 text-center bg-slate-850 border border-slate-750 rounded-xl">
                        <span className="material-symbols-outlined text-3xl text-indigo-400 animate-spin mb-3">sync</span>
                        <p className="text-slate-300 font-medium">Analyzing records...</p>
                        <p className="text-slate-500 text-xs mt-2">This may take a moment depending on the data size.</p>
                    </div>
                )}

                {!loadingPreview && !diffPreviewData && (
                    <div className="p-8 text-center bg-slate-850 border border-slate-750 border-dashed rounded-xl">
                        <span className="material-symbols-outlined text-4xl text-slate-600 mb-3">data_check</span>
                        <p className="text-slate-400">Click "Generate Impact Diff" to evaluate record changes.</p>
                    </div>
                )}

                {!loadingPreview && diffPreviewData && (
                    <div className="space-y-4">
                        {/* Use extracted DiffMetricCards component */}
                        <DiffMetricCards diffPreviewData={diffPreviewData} />

                        {/* Details Panel */}
                        <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden shadow-lg">
                            <div className="flex bg-slate-900 border-b border-slate-800 p-1">
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('changes')}
                                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${diffDetailTab === 'changes' ? 'bg-indigo-500/20 text-indigo-300' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800'}`}
                                >
                                    Calculated Changes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('qb_sample')}
                                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${diffDetailTab === 'qb_sample' ? 'bg-indigo-500/20 text-indigo-300' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800'}`}
                                >
                                    Target Sample Objects
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDiffDetailTab('pbs_sample')}
                                    className={`px-4 py-2 text-xs font-bold rounded-lg transition-colors ${diffDetailTab === 'pbs_sample' ? 'bg-indigo-500/20 text-indigo-300' : 'text-slate-400 hover:text-slate-300 hover:bg-slate-800'}`}
                                >
                                    Source ERP Sample
                                </button>
                            </div>
                            <div className="p-4">
                                {diffDetailTab === 'changes' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Transformations & Payload Diff ({diffPreviewData.sampleMappings?.length || 0})
                                        </h4>
                                        <div className="space-y-2">
                                            {(diffPreviewData.sampleMappings || []).map((sample, idx) => (
                                                <div key={idx} className="bg-slate-850 border border-slate-750 rounded-xl p-3 text-xs space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${sample.action === 'CREATE' ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' : 'bg-blue-950 text-blue-300 border border-blue-800'}`}>
                                                                {sample.action}
                                                            </span>
                                                            <span className="font-mono font-bold text-white">{sample.sourceKey}</span>
                                                            <span className="text-slate-400">&bull;</span>
                                                            <span className="text-slate-200 font-medium">{sample.sourceName}</span>
                                                        </div>
                                                    </div>
                                                    <pre className="p-2.5 bg-slate-950 rounded text-[11px] font-mono text-sky-300 overflow-x-auto border border-slate-800">
                                                        {JSON.stringify(sample.mappedPayload, null, 2)}
                                                    </pre>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {diffDetailTab === 'qb_sample' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Target Destination Sample Objects ({diffPreviewData.qbSampleRecords?.length || 0})
                                        </h4>
                                        <pre className="p-3 bg-slate-950 rounded-xl text-[11px] font-mono text-purple-300 overflow-x-auto border border-slate-800 max-h-96">
                                            {JSON.stringify(diffPreviewData.qbSampleRecords || [], null, 2)}
                                        </pre>
                                    </div>
                                )}

                                {diffDetailTab === 'pbs_sample' && (
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                                            Source ERP Export Extract ({diffPreviewData.pbsSampleRecords?.length || 0})
                                        </h4>
                                        <pre className="p-3 bg-slate-950 rounded-xl text-[11px] font-mono text-emerald-300 overflow-x-auto border border-slate-800 max-h-96">
                                            {JSON.stringify(diffPreviewData.pbsSampleRecords || [], null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
